(() => {
  const normalise = (value = "") => value.trim().toLowerCase();

  function formatMoney(cents) {
    return `Rs. ${(Number(cents || 0) / 100).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  function initApprovedPDP(root) {
    if (root.dataset.initialised === "true") return;
    root.dataset.initialised = "true";

    // The approved PDP always uses the customised cart drawer. Keep this
    // explicit because the legacy theme setting can still be false on stores
    // upgraded from the previous cart implementation.
    if (window.wcmmgSettings) window.wcmmgSettings.enable_cart_drawer = true;

    const sectionId = root.dataset.sectionId;
    const sticky = document.querySelector(`.ss-sticky-purchase[data-section-id="${sectionId}"]`);
    const productDataNode = root.querySelector("[data-product-json]");
    if (!productDataNode) return;

    let product;
    try {
      product = JSON.parse(productDataNode.textContent);
    } catch (error) {
      console.error("Sleep Scientist PDP product data could not be read.", error);
      return;
    }

    const optionGroups = [...root.querySelectorAll("[data-option-group]")];
    const variantInputs = [
      ...root.querySelectorAll("[data-variant-input]"),
      ...(sticky ? sticky.querySelectorAll("[data-variant-input]") : []),
    ];
    const mainQuantity = root.querySelector('.ss-main-product-form input[name="quantity"]');
    const stickyQuantity = sticky?.querySelector("[data-sticky-quantity]");
    const mainAdd = root.querySelector("[data-main-add]");
    const buyNow = root.querySelector("[data-buy-now]");
    const stickyBuyNow = sticky?.querySelector("[data-sticky-buy-now]");
    const price = root.querySelector("[data-product-price]");
    const stickyPrice = sticky?.querySelector("[data-sticky-price]");
    const purchaseMessage = root.querySelector(".ss-main-product-form .m-product-form-message");
    let magicCheckoutReady = false;
    const addedVariantIds = new Set();

    const galleryItems = [...root.querySelectorAll("[data-gallery-item]")];
    const galleryControls = [...root.querySelectorAll("[data-gallery-control]")];
    const galleryCount = root.querySelector("[data-gallery-count]");
    const mainMedia = root.querySelector(".ss-main-media");
    let visibleGalleryIndices = [];
    let activeGalleryIndex = Number(galleryItems.find((item) => item.classList.contains("is-active"))?.dataset.galleryIndex || 0);

    function selectedOptionValues() {
      return optionGroups.map((group) => group.querySelector(".ss-option-value.is-active")?.dataset.optionValue || "");
    }

    function selectedValueForGroup(match) {
      const group = optionGroups.find((item) => normalise(item.querySelector("legend")?.textContent).includes(match));
      return group?.querySelector(".ss-option-value.is-active")?.dataset.optionValue || "";
    }

    function activateGallery(index) {
      if (!visibleGalleryIndices.includes(index)) index = visibleGalleryIndices[0];
      if (typeof index !== "number") return;

      activeGalleryIndex = index;
      galleryItems.forEach((item) => {
        const isActive = Number(item.dataset.galleryIndex) === index;
        item.classList.toggle("is-active", isActive);
        item.hidden = !isActive;
      });
      galleryControls.forEach((control) => {
        control.classList.toggle("is-active", Number(control.dataset.galleryIndex) === index);
      });

      if (galleryCount) {
        const visiblePosition = visibleGalleryIndices.indexOf(index) + 1;
        galleryCount.textContent = `${visiblePosition} / ${visibleGalleryIndices.length}`;
      }
    }

    function filterGallery(colour) {
      const selectedColour = normalise(colour);
      visibleGalleryIndices = [];

      galleryControls.forEach((control) => {
        const controlColour = normalise(control.dataset.colour);
        const visible = controlColour === "__all__" || !selectedColour || controlColour === selectedColour;
        control.hidden = !visible;
        if (visible) visibleGalleryIndices.push(Number(control.dataset.galleryIndex));
      });

      visibleGalleryIndices = [...new Set(visibleGalleryIndices)];
      activateGallery(visibleGalleryIndices[0]);
    }

    function syncStickyQuantity() {
      if (!stickyQuantity || !mainQuantity) return;
      stickyQuantity.value = Math.max(1, Number(mainQuantity.value) || 1);
    }

    function updateSelectedLabels() {
      optionGroups.forEach((group) => {
        const value = group.querySelector(".ss-option-value.is-active")?.dataset.optionValue || "";
        const label = group.querySelector("[data-selected-option-label]");
        if (label) label.textContent = value;
      });

      if (!sticky) return;
      const colour = selectedValueForGroup("color") || selectedValueForGroup("colour") || selectedOptionValues()[0] || "";
      const size = selectedValueForGroup("size") || selectedOptionValues().find((value) => value !== colour) || colour;
      const colourNode = sticky.querySelector("[data-sticky-colour]");
      const sizeNode = sticky.querySelector("[data-sticky-size]");
      if (colourNode) colourNode.textContent = colour;
      if (sizeNode) sizeNode.textContent = size;
    }

    function updateVariant() {
      const values = selectedOptionValues();
      const variant = product.variants.find((candidate) => candidate.options.every((value, index) => value === values[index]));
      if (!variant) return;

      root.dataset.selectedVariantId = String(variant.id);
      variantInputs.forEach((input) => {
        input.value = variant.id;
        input.disabled = false;
      });
      if (price) price.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      const available = Boolean(variant.available);
      [mainAdd, buyNow, stickyBuyNow].forEach((button) => {
        if (!button) return;
        const isBuyNow = button === buyNow || button === stickyBuyNow;
        button.disabled = !available || (isBuyNow && !magicCheckoutReady);
        const label = button.querySelector("[data-add-label]");
        const isAdded = available && button === mainAdd && addedVariantIds.has(String(variant.id));
        button.classList.toggle("is-added", isAdded);
        if (label) label.textContent = available ? (isAdded ? "Added to Cart" : "Add to Cart") : "Sold Out";
        const buyLabel = button.querySelector("[data-buy-label]");
        if (buyLabel) buyLabel.textContent = available ? "Buy Now" : "Sold Out";
      });

      updateSelectedLabels();
      filterGallery(selectedValueForGroup("color") || selectedValueForGroup("colour"));

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url.toString());
    }

    optionGroups.forEach((group) => {
      group.addEventListener("click", (event) => {
        const option = event.target.closest(".ss-option-value");
        if (!option || !group.contains(option)) return;
        group.querySelectorAll(".ss-option-value").forEach((item) => item.classList.remove("is-active"));
        option.classList.add("is-active");
        updateVariant();
      });
    });

    galleryControls.forEach((control) => {
      control.addEventListener("click", () => activateGallery(Number(control.dataset.galleryIndex)));
    });

    function moveGallery(step) {
      if (!visibleGalleryIndices.length) return;
      const position = visibleGalleryIndices.indexOf(activeGalleryIndex);
      activateGallery(visibleGalleryIndices[(position + step + visibleGalleryIndices.length) % visibleGalleryIndices.length]);
    }

    root.querySelector("[data-gallery-previous]")?.addEventListener("click", () => {
      moveGallery(-1);
    });
    root.querySelector("[data-gallery-next]")?.addEventListener("click", () => {
      moveGallery(1);
    });

    if (mainMedia) {
      let swipeStartX = null;
      let swipeStartY = null;
      let swipePointerId = null;

      mainMedia.addEventListener("pointerdown", (event) => {
        if (!event.isPrimary || !window.matchMedia("(max-width: 900px)").matches) return;
        swipePointerId = event.pointerId;
        swipeStartX = event.clientX;
        swipeStartY = event.clientY;
      }, { passive: true });

      mainMedia.addEventListener("pointerup", (event) => {
        if (event.pointerId !== swipePointerId || swipeStartX === null || swipeStartY === null) return;
        const distanceX = event.clientX - swipeStartX;
        const distanceY = event.clientY - swipeStartY;
        const threshold = Math.max(36, mainMedia.clientWidth * 0.12);

        if (Math.abs(distanceX) >= threshold && Math.abs(distanceX) > Math.abs(distanceY) * 1.2) {
          moveGallery(distanceX < 0 ? 1 : -1);
        }

        swipePointerId = null;
        swipeStartX = null;
        swipeStartY = null;
      }, { passive: true });

      mainMedia.addEventListener("pointercancel", () => {
        swipePointerId = null;
        swipeStartX = null;
        swipeStartY = null;
      }, { passive: true });
    }

    mainQuantity?.addEventListener("input", syncStickyQuantity);
    mainQuantity?.addEventListener("change", syncStickyQuantity);
    root.querySelector(".ss-quantity-row")?.addEventListener("click", () => window.requestAnimationFrame(syncStickyQuantity));

    root.querySelector("[data-size-chart]")?.addEventListener("click", () => {
      const details = root.querySelector("[data-size-details]");
      if (!details) return;
      details.open = true;
      details.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });

    document.addEventListener("product-ajax:added", (event) => {
      const addedProduct = event.detail?.product;
      const addedVariantId = String(addedProduct?.variant_id || addedProduct?.id || "");
      const selectedVariantId = String(root.dataset.selectedVariantId || "");
      if (!addedVariantId || addedVariantId !== selectedVariantId) return;

      addedVariantIds.add(addedVariantId);
      mainAdd?.classList.add("is-added");
      const label = mainAdd?.querySelector("[data-add-label]");
      if (label) label.textContent = "Added to Cart";
    });

    function markMagicCheckoutReady() {
      const listenerAttached = window.performance
        ?.getEntriesByName("listener-attached")
        .some((entry) => entry.entryType === "mark");

      if (listenerAttached) {
        magicCheckoutReady = true;
        if (purchaseMessage) purchaseMessage.textContent = "";
        updateVariant();
        return;
      }

      window.setTimeout(markMagicCheckoutReady, 100);
    }

    markMagicCheckoutReady();

    const primaryPurchase = root.querySelector("[data-primary-purchase]");
    function updateStickyVisibility() {
      if (!sticky || !primaryPurchase) return;
      const visible = primaryPurchase.getBoundingClientRect().bottom < 80;
      sticky.classList.toggle("is-visible", visible);
      sticky.setAttribute("aria-hidden", String(!visible));
    }
    updateStickyVisibility();
    window.addEventListener("scroll", updateStickyVisibility, { passive: true });

    const reviewTrack = root.querySelector("[data-review-track]");
    const reviewCounter = root.querySelector("[data-review-counter]");
    let reviewIndex = 0;
    const reviewCount = reviewTrack?.children.length || 0;
    function updateReviewCounter() {
      if (reviewCounter) reviewCounter.textContent = `${reviewIndex + 1} / ${reviewCount}`;
    }
    root.querySelector("[data-review-next]")?.addEventListener("click", () => {
      if (!reviewTrack || !reviewCount) return;
      reviewIndex = (reviewIndex + 1) % reviewCount;
      if (window.innerWidth > 900) reviewTrack.append(reviewTrack.firstElementChild);
      else reviewTrack.scrollBy({ left: reviewTrack.clientWidth * .8, behavior: "smooth" });
      updateReviewCounter();
    });
    root.querySelector("[data-review-previous]")?.addEventListener("click", () => {
      if (!reviewTrack || !reviewCount) return;
      reviewIndex = (reviewIndex - 1 + reviewCount) % reviewCount;
      if (window.innerWidth > 900) reviewTrack.prepend(reviewTrack.lastElementChild);
      else reviewTrack.scrollBy({ left: reviewTrack.clientWidth * -.8, behavior: "smooth" });
      updateReviewCounter();
    });

    updateVariant();
    syncStickyQuantity();
    updateReviewCounter();
  }

  function recommendationLabel(product) {
    const tags = Array.isArray(product.tags) ? product.tags : [];
    if (tags.includes("Cool & Light")) return "Cool & Light";
    if (tags.includes("Everyday Comfort")) return "Everyday Comfort";
    if (tags.includes("Cozy Warmth")) return "Cozy Warmth";
    return "";
  }

  function initRecommendations(section) {
    if (section.dataset.recommendationsInitialised === "true") return;
    section.dataset.recommendationsInitialised = "true";

    const grid = section.querySelector("[data-recommendation-grid]");
    const template = section.querySelector("[data-recommendation-card-template]");
    if (!grid || !template || !section.dataset.url) return;

    const seenHandles = new Set(
      [...grid.querySelectorAll("[data-product-handle]")]
        .map((card) => card.dataset.productHandle)
        .filter(Boolean),
    );

    fetch(section.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error("Recommendations could not be loaded.");
        return response.json();
      })
      .then(({ products = [] }) => {
        for (const product of products) {
          if (grid.children.length >= 6) break;
          if (!product?.handle || seenHandles.has(product.handle)) continue;

          const fragment = template.content.cloneNode(true);
          const card = fragment.querySelector("[data-product-handle]");
          const links = fragment.querySelectorAll("a");
          const image = fragment.querySelector("img");
          const label = fragment.querySelector(".ss-comfort-tag");

          card.dataset.productHandle = product.handle;
          links.forEach((link) => { link.href = product.url; });
          image.src = product.featured_image;
          image.alt = product.title;
          fragment.querySelector("h3 a").textContent = product.title;
          fragment.querySelector(".ss-product-card > p").textContent = formatMoney(product.price);

          const comfortLabel = recommendationLabel(product);
          label.textContent = comfortLabel;
          label.dataset.comfort = comfortLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
          label.hidden = !comfortLabel;

          grid.append(fragment);
          seenHandles.add(product.handle);
        }
      })
      .catch(() => {
        // Keep the always-on blanket recommendations already rendered by Liquid.
      });
  }

  function initialiseAll() {
    document.querySelectorAll(".ss-approved-pdp").forEach(initApprovedPDP);
    document.querySelectorAll("[data-ss-recommendations][data-url]").forEach(initRecommendations);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialiseAll);
  else initialiseAll();

  document.addEventListener("shopify:section:load", initialiseAll);
})();
